import { useState } from "react";
import { Box } from "@mui/material";
import { WarningAmber, InfoOutlined, Block } from "@mui/icons-material";
import { ReservedSlotData, BookingSlot } from "../types";
import { SlotsPopoverInformation, toPopoverBooking } from "../shared/SlotsPopoverInformation";
import { WARNING_RED } from "../constants";
import {
    FREE_GREEN,
    FREE_TEXT_GREEN,
    NFB_FULL_BG,
    NFB_BLOCKED_GREY,
    BLOCKED_TEXT_GREY,
    NFB_BORDER,
    NFB_SESSION_ONLY_BG,
    NFB_SESSION_ONLY_TEXT,
    NfbBlockStatus,
    getNfbBlockStatus,
} from "./gridUtils";

type NfbBlockProps = {
    reservations: Array<ReservedSlotData>;
    bookings: Array<BookingSlot>;
    capacity: number;
    isStartRow: boolean;
    isEndRow: boolean;
};

// Map the bookability status (rule lives in gridUtils.getNfbBlockStatus) to its colour pair + hover hint.
const STATUS_STYLE: Record<NfbBlockStatus, { background: string; text: string }> = {
    blocked: { background: NFB_BLOCKED_GREY, text: BLOCKED_TEXT_GREY },
    full: { background: NFB_FULL_BG, text: WARNING_RED },
    sessionOnly: { background: NFB_SESSION_ONLY_BG, text: NFB_SESSION_ONLY_TEXT },
    free: { background: FREE_GREEN, text: FREE_TEXT_GREEN },
};

const STATUS_TOOLTIP: Record<NfbBlockStatus, string> = {
    blocked: "Horário bloqueado para Neurofeedback",
    full: "Sem aparelhos livres nesta data — não é possível marcar",
    sessionOnly: "Reservas esgotadas, mas há aparelho livre — podes marcar sessão",
    free: "Há vaga para reserva",
};

export const NfbBlock = ({ reservations, bookings, capacity, isStartRow, isEndRow }: NfbBlockProps) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const status = getNfbBlockStatus(capacity, reservations.length, bookings.length);
    const colors = STATUS_STYLE[status];
    const hasMissedSessions = reservations.some((reservation) => reservation.missedTwoConsecutiveSessions);

    const handleBlockClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                onClick={handleBlockClick}
                title={STATUS_TOOLTIP[status]}
                sx={{
                    backgroundColor: colors.background,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderLeft: `1px solid ${NFB_BORDER}`,
                    borderRight: `1px solid ${NFB_BORDER}`,
                    borderTop: isStartRow ? `1px solid ${NFB_BORDER}` : "none",
                    borderBottom: isEndRow ? `1px solid ${NFB_BORDER}` : "none",
                    cursor: "pointer",
                }}
            >
                {isStartRow &&
                    (hasMissedSessions ? (
                        <WarningAmber sx={{ fontSize: 18, color: WARNING_RED }} />
                    ) : status === "blocked" ? (
                        <Block sx={{ fontSize: 16, color: BLOCKED_TEXT_GREY }} />
                    ) : (
                        <InfoOutlined sx={{ fontSize: 17, color: colors.text, opacity: 0.7 }} />
                    ))}
            </Box>
            <SlotsPopoverInformation
                anchorEl={anchorEl}
                onClose={handlePopoverClose}
                reservations={reservations.map((reservation) => ({
                    clientId: reservation.clientId,
                    clientName: reservation.clientName,
                    treatmentId: reservation.treatmentId,
                    missedTwoConsecutiveSessions: reservation.missedTwoConsecutiveSessions,
                }))}
                bookings={bookings.map(toPopoverBooking)}
            />
        </>
    );
};
